/**
 * Recovering from a deploy while the app is open. Each build has new hashed chunk names and the
 * old ones are gone, so a lazy route or a preload then fails to load. The first failure reloads
 * the page (index.html is served with no-cache, so the reload picks up the new build); a second
 * failure within a minute of that reload shows "A new version is available" with a Reload button
 * instead, so a broken deploy cannot put the page into a reload loop.
 */

export const RELOAD_KEY = 'outss:chunk-reload-at';
export const RELOAD_WINDOW_MS = 60_000;

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * What to do after a chunk failed to load: reload once, or prompt when the page already reloaded
 * for this less than a minute ago. Without working storage there is no loop guard, so it prompts.
 */
export function onChunkError(store: KeyValueStore | null, now: number): 'reload' | 'prompt' {
  if (!store) return 'prompt';
  try {
    const last = Number(store.getItem(RELOAD_KEY));
    if (last > 0 && now - last >= 0 && now - last < RELOAD_WINDOW_MS) return 'prompt';
    store.setItem(RELOAD_KEY, String(now));
    return 'reload';
  } catch {
    return 'prompt';
  }
}

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk .* failed/i;

/** Whether an error is a lazy chunk or preload that failed to load. */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return CHUNK_ERROR.test(message);
}

// ---- the "new version" prompt, shown by NewVersionBanner ----
let prompting = false;
const listeners = new Set<() => void>();

export const staleBuild = {
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  get: () => prompting,
};

function session(): KeyValueStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Reloads once, or shows the prompt (see onChunkError). */
export function recoverFromChunkError(): void {
  if (onChunkError(session(), Date.now()) === 'reload') {
    window.location.reload();
    return;
  }
  if (!prompting) {
    prompting = true;
    listeners.forEach((l) => l());
  }
}

/** Vite fires vite:preloadError when a preloaded chunk or stylesheet of a lazy import fails. */
export function installStaleBuildRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    recoverFromChunkError();
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) recoverFromChunkError();
  });
}
